import clsx from 'clsx'
import {useCallback, useEffect, useLayoutEffect, useRef, useState} from 'react'
import {Link, useLocation} from 'react-router-dom'
import {CSSTransition} from 'react-transition-group'
import {useAuth} from '../../../../app/modules/auth'
import {ROUTES} from '../../../constants'
import {allowTestOnly, KTSVG} from '../../../helpers'
import {useIntl} from 'react-intl'
import {SwitchCompany} from '../../../helpers/components/SwitchCompany'
import useWindowDimensions from '../../../hooks/useWindowDimensions'

const AccountButton = () => {
  const {currentUser} = useAuth()
  const [showMenu, setShowMenu] = useState(false)
  const containerRef = useRef<HTMLDivElement | null>(null)
  const buttonRef = useRef<HTMLButtonElement | null>(null)
  const [transformOrigin, setTransformOrigin] = useState('top right')
  const intl = useIntl()
  const {pathname} = useLocation()
  const {isMobileTablet, isWindows} = useWindowDimensions()

  const calculateTransformOrigin = useCallback(() => {
    if (!buttonRef.current) return 'top right'

    const buttonRect = buttonRef.current.getBoundingClientRect()
    const buttonCenterX = buttonRect.left + buttonRect.width / 2
    return buttonCenterX > window.innerWidth / 2 ? 'top right' : 'top left'
  }, [])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setShowMenu(false)
      }
    }

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setShowMenu(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleEscape)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleEscape)
    }
  }, [])

  useLayoutEffect(() => {
    if (!showMenu) return

    const updateTransformOrigin = () => {
      setTransformOrigin(calculateTransformOrigin())
    }

    updateTransformOrigin()
    window.addEventListener('resize', updateTransformOrigin)

    return () => {
      window.removeEventListener('resize', updateTransformOrigin)
    }
  }, [showMenu, calculateTransformOrigin])

  if (!currentUser) return null

  const fullName =
    [currentUser.first_name, currentUser.last_name].filter(Boolean).join(' ') ||
    currentUser.username
  const planLabel = currentUser.plan
    ? `${currentUser.plan.charAt(0).toUpperCase()}${currentUser.plan.slice(1).toLowerCase()}`
    : 'Free'
  const isActive = showMenu || pathname === ROUTES.SUPPORT.BASE

  return (
    <div className='topbar-dropdown topbar-dropdown--account' ref={containerRef}>
      <button
        type='button'
        id='gori-account-dropdown'
        ref={buttonRef}
        className={clsx('btn p-0 ms-2 position-relative topbar-icon-button', {
          'topbar-icon-button--active': isActive,
        })}
        aria-expanded={showMenu}
        aria-haspopup='menu'
        onClick={() => setShowMenu((prev) => !prev)}
      >
        <KTSVG
          path='/media/gori/topbar/account.svg'
          className='desktop-only-menu-item'
          small={false}
        />
      </button>

      <CSSTransition in={showMenu} timeout={600} classNames='pop' unmountOnExit>
        <div
          className='topbar-dropdown-menu topbar-dropdown-menu--account'
          role='menu'
          aria-labelledby='gori-account-dropdown'
          style={{borderRadius: '8px', transformOrigin}}
        >
          <div className='topbar-dropdown-menu__header'>
            <div className='d-flex align-items-start justify-content-between gap-3'>
              <div className='min-w-0'>
                <div className='topbar-dropdown-menu__name text-truncate'>{fullName}</div>
              </div>
              <span className='topbar-dropdown-menu__plan'>{planLabel}</span>
            </div>

            <div className='my-2'>
              {allowTestOnly(currentUser) && (
                <span className='fs-7 fw-bold text-white'>
                  {intl.formatMessage({id: 'CURRENT_VERSION'})}: #{process.env.REACT_APP_COMMIT_ID}
                </span>
              )}
            </div>
            <SwitchCompany
              className='bg-white rounded-2 mt-4'
              classNameMenu={clsx({'me-12': isMobileTablet, 'ms-12': isWindows})}
            />
          </div>

          <div className='topbar-dropdown-menu__content'>
            <Link
              to={ROUTES.SETTINGS.PROFILE}
              className='dropdown-item topbar-dropdown-menu__item'
              onClick={() => setShowMenu(false)}
            >
              {intl.formatMessage({id: 'PROFILE'})}
            </Link>
            <Link
              to={ROUTES.SUPPORT.BASE}
              className='dropdown-item topbar-dropdown-menu__item'
              onClick={() => setShowMenu(false)}
            >
              {intl.formatMessage({id: 'SUPPORT'})}
            </Link>
            <Link
              to={ROUTES.AUTH.LOGOUT}
              className='dropdown-item topbar-dropdown-menu__item topbar-dropdown-menu__item--logout'
              onClick={() => setShowMenu(false)}
            >
              {intl.formatMessage({id: 'SIGN_OUT'})}
            </Link>
          </div>
        </div>
      </CSSTransition>
    </div>
  )
}

export {AccountButton}
